import { CircleStop, Fuel, LayoutGrid, RefreshCw, Table2 } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { selectIsAdmin, useAuthStore } from '@/features/auth/auth.store';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatCurrency, formatDateTimeValue, formatHoraValue } from '@/lib/utils';

import {
  colorProducto,
  datosLado,
  esUltimoCombustible,
  estiloLado,
  galones,
  hayLadosEnServicio,
  ladoEnServicio,
  mangueraDeLado,
  mensajeControlador,
} from '../lib/monitoreo-live';
import { useControllerSetup, useParadaEmergencia, usePumpStatus } from '../monitoreo.api';
import type { ControllerSetup, LadoCatalogo, PaqueteLado } from '../monitoreo.types';
import { MonitoreoNav } from '../components/monitoreo-nav';

type Vista = 'tarjetas' | 'tabla';

/** Datos de la última transacción que se muestran en cada lado. */
interface ResumenLado {
  producto: string | null;
  volume: number;
  price: number;
  amount: number;
  transaction: number;
  totalizador: number;
  fecha: string | null;
}

/**
 * Resumen de la última operación del lado, según el estado que reporta el
 * controlador (misma lógica que el panel anterior).
 */
function resumenLado(paquete: PaqueteLado, lado: LadoCatalogo | undefined): ResumenLado {
  const datos = datosLado(paquete);

  switch (paquete.Type) {
    case 'PumpIdleStatus': {
      const levantada = datos.NozzleUp === 1;

      return {
        producto: mangueraDeLado(lado, levantada ? datos.Nozzle : datos.LastNozzle)?.producto ?? null,
        volume: Number(datos.LastVolume ?? 0),
        price: Number(datos.LastPrice ?? 0),
        amount: Number(datos.LastAmount ?? 0),
        transaction: Number(datos.LastTransaction ?? 0),
        totalizador: Number(datos.LastReceivedTotalVolume ?? 0),
        fecha: datos.LastDateTime ?? datos.LastDateTimeStart ?? null,
      };
    }

    case 'PumpFillingStatus':
      return {
        producto: mangueraDeLado(lado, datos.Nozzle)?.producto ?? datos.FuelGradeName ?? null,
        volume: Number(datos.Volume ?? 0),
        price: Number(datos.Price ?? 0),
        amount: Number(datos.Amount ?? 0),
        transaction: Number(datos.Transaction ?? 0),
        totalizador: Number(datos.LastReceivedTotalVolume ?? 0),
        fecha: datos.LastDateTime ?? null,
      };

    case 'PumpEndOfTransactionStatus':
      return {
        producto: mangueraDeLado(lado, datos.Nozzle)?.producto ?? datos.FuelGradeName ?? null,
        volume: Number(datos.LastVolume ?? datos.Volume ?? 0),
        price: Number(datos.LastPrice ?? datos.Price ?? 0),
        amount: Number(datos.LastAmount ?? datos.Amount ?? 0),
        transaction: Number(datos.LastTransaction ?? datos.Transaction ?? 0),
        totalizador: Number(datos.LastReceivedTotalVolume ?? 0),
        fecha: datos.LastDateTime ?? null,
      };

    default:
      return {
        producto: null,
        volume: 0,
        price: 0,
        amount: 0,
        transaction: 0,
        totalizador: Number(datos.LastReceivedTotalVolume ?? 0),
        fecha: null,
      };
  }
}

/** Combustibles que puede despachar el lado, según los precios por manguera. */
function combustiblesDeLado(
  paquete: PaqueteLado,
  lado: LadoCatalogo | undefined,
): { nozzle: number; producto: string; color: string | null; precio: number; ultimo: boolean }[] {
  const datos = datosLado(paquete);
  const ultimo = datos.LastFuelGradeName;

  return (datos.NozzlePrices ?? [])
    .map((precio, indice) => {
      if (!precio || precio <= 0) return null;

      const manguera = mangueraDeLado(lado, indice + 1);

      return {
        nozzle: indice + 1,
        producto: manguera?.producto ?? `Manguera ${indice + 1}`,
        color: manguera?.color ?? null,
        precio,
        ultimo: esUltimoCombustible(manguera?.producto ?? null, ultimo),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}

/** Tarjeta de un lado del surtidor. */
function TarjetaLado({
  paquete,
  lado,
  setup,
  onDetener,
}: {
  paquete: PaqueteLado;
  lado: LadoCatalogo | undefined;
  setup: ControllerSetup | undefined;
  onDetener: (lado: number) => void;
}) {
  const datos = datosLado(paquete);
  const estilo = estiloLado(paquete);
  const resumen = resumenLado(paquete, lado);
  const enServicio = ladoEnServicio(paquete);
  const despachando = paquete.Type === 'PumpFillingStatus';
  const esperando = paquete.Type === 'PumpEndOfTransactionStatus';
  const combustibles = combustiblesDeLado(paquete, lado);

  // El tanque que abastece la manguera del producto en uso (si está configurado).
  const tanque = setup?.tanques.find((item) => item.producto === resumen.producto);

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className={cn('flex items-center justify-between gap-2 border-b px-4 py-3', estilo.cabecera)}>
        <div className="flex min-w-0 items-center gap-2">
          <Fuel className="size-4 shrink-0 text-ink-700" aria-hidden="true" />
          <span className="truncate text-sm font-bold text-ink-900">
            {lado?.nombre ?? `LADO ${datos.Pump}`}
          </span>
        </div>

        <span className="flex flex-wrap items-center gap-2">
          <Badge variant={enServicio ? 'success' : 'danger'}>{enServicio ? 'Conectado' : 'Desconectado'}</Badge>
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('size-2.5 shrink-0 rounded-full', estilo.punto)} aria-hidden="true" />
          <Badge variant={estilo.variante}>{estilo.etiqueta}</Badge>
          {tanque ? (
            <span className="truncate text-xs text-ink-500" title={`Tanque ${tanque.id_tank}`}>
              Tanque {tanque.id_tank}
            </span>
          ) : null}
        </div>

        {despachando || esperando ? (
          <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
              {despachando ? 'Despachando ahora' : 'Producto entregado'}
            </p>
            <p
              className="mt-0.5 text-sm font-bold"
              style={{ color: colorProducto(tanque?.color ?? null, '#0f172a') }}
            >
              {resumen.producto ?? 'Sin identificar'}
            </p>
          </div>
        ) : (
          <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
              Combustibles
            </p>

            {combustibles.length === 0 ? (
              <p className="mt-0.5 text-xs text-ink-500">Sin combustibles configurados</p>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {combustibles.map((combustible) => (
                  <span
                    key={combustible.nozzle}
                    title={`Manguera ${combustible.nozzle}`}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                      combustible.ultimo
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                        : 'border-ink-200 bg-white text-ink-700',
                    )}
                  >
                    <span
                      className="size-2 rounded-full"
                      style={{ backgroundColor: colorProducto(combustible.color) }}
                      aria-hidden="true"
                    />
                    {combustible.producto}
                    <span className="font-normal text-ink-500">
                      {formatCurrency(combustible.precio)}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Precio</dt>
            <dd className="text-ink-900">{formatCurrency(resumen.price)}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Volumen</dt>
            <dd className="text-ink-900">{galones(resumen.volume)} gal</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Monto</dt>
            <dd className="font-semibold text-brand-700">{formatCurrency(resumen.amount)}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              Comprobante
            </dt>
            <dd className="text-ink-900">#{resumen.transaction || 0}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              Totalizador
            </dt>
            <dd className="text-ink-700">{galones(resumen.totalizador)} gal</dd>
          </div>
        </dl>

        <p className="mt-auto text-xs text-ink-500">
          {resumen.fecha ? `Última operación: ${formatDateTimeValue(resumen.fecha)}` : 'Sin operaciones registradas'}
        </p>
      </div>

      <div className="border-t border-ink-100 px-4 py-3">
        <Button
          size="sm"
          variant="danger"
          className="w-full"
          onClick={() => onDetener(datos.Pump)}
          disabled={!enServicio}
        >
          <CircleStop className="size-3.5" aria-hidden="true" />
          Detener lado
        </Button>
      </div>
    </Card>
  );
}

/**
 * Surtidor en vivo (`/monitoreo/surtidor`).
 *
 * Puerto de `Monitoring::index()`: el estado de cada lado se consulta al
 * controlador cada pocos segundos y la parada de emergencia se envía al
 * controlador (sólo administradores).
 */
export function SurtidorVivoPage() {
  useDocumentTitle('Monitoreo · Surtidor en vivo');

  const esAdmin = useAuthStore(selectIsAdmin);
  const [vista, setVista] = useState<Vista>('tarjetas');
  const [porDetener, setPorDetener] = useState<number[] | null>(null);

  const setup = useControllerSetup();
  const parada = useParadaEmergencia();

  const lados = setup.data?.lados.map((lado) => lado.pump) ?? [];
  const estado = usePumpStatus(lados, Boolean(setup.data));

  const paquetes = estado.data?.packets ?? [];
  const hayServicio = hayLadosEnServicio(paquetes);

  const confirmarDetencion = async () => {
    if (!porDetener) return;

    await parada.mutateAsync(porDetener);
    setPorDetener(null);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Fuel className="size-6 text-brand-500" aria-hidden="true" />
            Surtidor en vivo
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Estado de cada lado del controlador, con la última operación registrada. Se
            actualiza automáticamente cada 3 segundos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-control border border-ink-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => setVista('tarjetas')}
              aria-pressed={vista === 'tarjetas'}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 text-xs font-semibold transition-colors',
                vista === 'tarjetas' ? 'bg-ink-900 text-white' : 'text-ink-600 hover:text-ink-900',
              )}
            >
              <LayoutGrid className="size-3.5" aria-hidden="true" />
              Tarjetas
            </button>
            <button
              type="button"
              onClick={() => setVista('tabla')}
              aria-pressed={vista === 'tabla'}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 text-xs font-semibold transition-colors',
                vista === 'tabla' ? 'bg-ink-900 text-white' : 'text-ink-600 hover:text-ink-900',
              )}
            >
              <Table2 className="size-3.5" aria-hidden="true" />
              Tabla
            </button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void estado.refetch()}
            loading={estado.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>

          {esAdmin ? (
            <Button
              variant="danger"
              size="sm"
              disabled={!hayServicio}
              onClick={() => setPorDetener(lados)}
            >
              <CircleStop className="size-3.5" aria-hidden="true" />
              Parada de emergencia
            </Button>
          ) : null}
        </div>
      </header>

      <MonitoreoNav active="surtidor" />

      {estado.data?.actualizado ? (
        <p className="text-xs text-ink-500">
          Última lectura del controlador: {formatHoraValue(estado.data.actualizado)}
          {estado.data.sincronizados > 0
            ? ` · ${estado.data.sincronizados} manguera(s) sincronizada(s)`
            : null}
        </p>
      ) : null}

      {parada.isError ? (
        <Alert variant="error" title="No se pudo detener los lados">
          {mensajeControlador(parada.error)}
        </Alert>
      ) : null}

      {parada.isSuccess && porDetener === null ? (
        <Alert variant="success" title="Parada enviada">
          {parada.data.message}
        </Alert>
      ) : null}

      {setup.isPending || estado.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((clave) => (
            <Skeleton key={clave} className="h-72 rounded-card" />
          ))}
        </div>
      ) : estado.isError ? (
        <Alert variant="error" title="No se pudo consultar el controlador">
          <div className="flex flex-wrap items-center gap-3">
            <span>{mensajeControlador(estado.error)}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void estado.refetch()}
              loading={estado.isFetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : vista === 'tarjetas' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {paquetes.map((paquete) => (
            <TarjetaLado
              key={paquete.Id}
              paquete={paquete}
              lado={setup.data?.lados.find((lado) => lado.pump === datosLado(paquete).Pump)}
              setup={setup.data}
              onDetener={(lado) => setPorDetener([lado])}
            />
          ))}
        </div>
      ) : (
        <SectionCard
          title="Lados del controlador"
          description="Estado, última operación y contómetro de cada lado."
        >
          <div className="overflow-x-auto">
            <div className="min-w-[74rem]">
              <div className="grid grid-cols-[5rem_11rem_minmax(0,1fr)_7rem_6.5rem_7rem_8rem_6rem_9rem] items-center gap-x-3 border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                <span>Lado</span>
                <span>Estado</span>
                <span>Producto</span>
                <span className="text-right">Volumen</span>
                <span className="text-right">Precio</span>
                <span className="text-right">Monto</span>
                <span className="text-right">Totalizador</span>
                <span className="text-right">Comprob.</span>
                <span className="text-right">Acción</span>
              </div>

              {paquetes.map((paquete) => {
                const datos = datosLado(paquete);
                const lado = setup.data?.lados.find((item) => item.pump === datos.Pump);
                const resumen = resumenLado(paquete, lado);
                const estilo = estiloLado(paquete);
                const enServicio = ladoEnServicio(paquete);

                return (
                  <div
                    key={paquete.Id}
                    className="grid grid-cols-[5rem_11rem_minmax(0,1fr)_7rem_6.5rem_7rem_8rem_6rem_9rem] items-center gap-x-3 border-b border-ink-50 py-2 text-sm text-ink-800"
                  >
                    <span className="font-semibold">{lado?.nombre ?? `LADO ${datos.Pump}`}</span>
                    <span>
                      <Badge variant={estilo.variante}>{estilo.etiqueta}</Badge>
                    </span>
                    <span className="truncate">{resumen.producto ?? 'Sin datos'}</span>
                    <span className="text-right tabular-nums">{galones(resumen.volume)}</span>
                    <span className="text-right tabular-nums">{formatCurrency(resumen.price)}</span>
                    <span className="text-right font-semibold tabular-nums">
                      {formatCurrency(resumen.amount)}
                    </span>
                    <span className="text-right tabular-nums">
                      {galones(resumen.totalizador)}
                    </span>
                    <span className="text-right tabular-nums">#{resumen.transaction || 0}</span>
                    <span className="flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!enServicio || !esAdmin}
                        onClick={() => setPorDetener([datos.Pump])}
                      >
                        Detener
                      </Button>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>
      )}

      <ConfirmDialog
        open={porDetener !== null}
        title={
          porDetener?.length === 1
            ? `Detener el lado ${porDetener[0]}`
            : `Detener ${porDetener?.length ?? 0} lados`
        }
        description="Se enviará la orden de parada de emergencia al controlador y el despacho se detendrá de inmediato."
        confirmLabel="Sí, detener"
        loading={parada.isPending}
        onConfirm={() => void confirmarDetencion()}
        onClose={() => setPorDetener(null)}
      >
        <p className="text-sm text-ink-600">
          Lados: <strong>{porDetener?.join(', ')}</strong>
        </p>
      </ConfirmDialog>
    </div>
  );
}
